/**
 * Lightweight product-analytics SDK. Queues events and flushes them batched to
 * POST /api/events (server derives player_id from the JWT). No third party.
 */
import { getToken } from './api/client';
import { getStartParam, isTelegram, tgPlatform, tgVersion } from './telegram';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const ENDPOINT = `${BASE}/api/events`;
const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev';
const FLUSH_MS = 5000;
const MAX_QUEUE = 50;

type Props = Record<string, unknown>;
interface QueuedEvent {
  name: string;
  props: Props;
  sessionId: string;
}

function makeSessionId(): string {
  const KEY = 'ante_sid';
  try {
    let sid = sessionStorage.getItem(KEY);
    if (!sid) {
      sid = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(KEY, sid);
    }
    return sid;
  } catch {
    return 'nosession';
  }
}
const sessionId = makeSessionId();

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function send(events: QueuedEvent[], onUnload = false): void {
  if (!events.length) return;
  const body = JSON.stringify({ sessionId, events });
  const token = getToken();
  // sendBeacon can't set Authorization → only use it for anonymous unload flushes.
  if (onUnload && !token && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    } catch {
      /* fall through to fetch */
    }
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  void fetch(ENDPOINT, { method: 'POST', headers, body, keepalive: true }).catch(() => {});
}

function flush(onUnload = false): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!queue.length) return;
  const batch = queue;
  queue = [];
  send(batch, onUnload);
}

/** Enqueue an event (batched, fire-and-forget). */
export function track(name: string, props: Props = {}): void {
  queue.push({ name, props, sessionId });
  if (queue.length >= MAX_QUEUE) {
    flush();
    return;
  }
  if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
}

let started = false;
export function initAnalytics(): void {
  if (started) return;
  started = true;
  track('app_open', {
    startParam: getStartParam(),
    platform: tgPlatform,
    tgVersion,
    isTelegram,
    appVersion: APP_VERSION,
  });
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush(true);
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => flush(true));
  }
}
